import { type PropsWithChildren, useMemo, useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { type ColumnDef, type RowSelectionState } from "@tanstack/react-table";
import { DownloadIcon } from "vega-icons";
import { ListIllustration } from "vega-illustrations";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useCloseDealOpportunities } from "@vega-api/generated/coinvest-deal-opportunity-actions/coinvest-deal-opportunity-actions";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { useGetCoInvestGpPermissions } from "@vega-api/generated/coinvest-gp-permissions/coinvest-gp-permissions";
import { type Currency } from "@vega-api/generated/types/currency";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { type DealTrackingTotals } from "@vega-api/generated/types/dealTrackingTotals";
import { BulkActions } from "@vega-core/components/BulkActions/BulkActions";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { CounterBadge } from "@vega-core/components/CounterBadge/CounterBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@vega-core/components/shadcn/dropdown-menu";
import { ACTION_COLUMN_DEFAULT_PROPS } from "@vega-core/components/Table/CoreTable/constants";
import { useTableSelection } from "@vega-core/components/Table/CoreTable/hooks/useTableSelection";
import { GroupedHeaderTable } from "@vega-core/components/Table/GroupedHeaderTable/GroupedHeaderTable";
import { type ColumnGroup } from "@vega-core/components/Table/types";
import { useToast } from "@vega-core/components/Toast/useToast";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { formatMoneyShort } from "@vega-core/utils/formatMoney";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { useTableFilters } from "@vega-modules/components/Table/TableFilters/hooks/useTableFilters";
import {
  FilterType,
  TableFilterId,
  type TableFiltersConfig,
} from "@vega-modules/components/Table/TableFilters/types";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { CoInvestmentFullPaths } from "@vega-modules/paths";
import { TrackingTag } from "@vega-modules/scenes/CoInvestmentsGP/components/TrackingTable/components/TrackingTag";
import { useExportFilename } from "@vega-modules/scenes/CoInvestmentsGP/hooks/useExportFilename";
import { useTableExport } from "@vega-modules/scenes/CoInvestmentsGP/hooks/useTableExport";

import { AllocationAmountModal } from "./components/AllocationAmountModal";
import { BulkCloseDealModal } from "./components/BulkCloseDealModal";
import { EditEstimatedMaxTicketSizeModal } from "./components/EditEstimatedMaxTicketSizeModal";
import { EditEstimatedMinTicketSizeModal } from "./components/EditEstimatedMinTicketSizeModal";
import { EditIndicationOfInterestModal } from "./components/EditIndicationOfInterestModal";
import { EditIndicativeOrderModal } from "./components/EditIndicativeOrderModal";
import { EditMaxTicketSizeModal } from "./components/EditMaxTicketSizeModal";
import { EditMinTicketSizeModal } from "./components/EditMinTicketSizeModal";
import { TrackingRowActionsMenu } from "./components/TrackingRowActionsMenu";
import { useTrackingExportColumns } from "./hooks/useTrackingExportColumns";
import {
  canOpenModalForItem,
  useTrackingModals,
} from "./hooks/useTrackingModals";
import {
  createCellWithTotalSupport,
  isTotalRow,
  TOTAL_ROW_ID,
} from "./utils/cellHelpers";
import {
  formatAllocationAmountCellText,
  formatCommitmentAmountCellText,
  formatGpEstimatedTicketSizeMaxCellText,
  formatGpEstimatedTicketSizeMinCellText,
  formatGpIndicativeOrderCellText,
  formatIndicativeAmountCellText,
  formatLimitedPartnerCellText,
  formatLpTicketSizeMaxCellText,
  formatLpTicketSizeMinCellText,
  formatNdaCellText,
  formatStageCellText,
} from "./utils/formatCellContent";
import { areAllNdasSigned, hasUnsignedNdas } from "./utils/ndaHelpers";

function RejectDetailsTooltip({
  children,
  rejectComment,
  rejectReason,
  gpRejectReason,
  gpRejectSubreason,
}: PropsWithChildren<{
  rejectComment?: string;
  rejectReason?: string;
  gpRejectReason?: string;
  gpRejectSubreason?: string;
}>) {
  // GP and LP rejections are mutually exclusive — show whichever is present
  const title = (() => {
    if (rejectReason) {
      return m.coInvestments_tracking_reject_tooltipReason({ rejectReason });
    }

    if (gpRejectReason) {
      return m.coInvestments_tracking_reject_tooltipReason({
        rejectReason: gpRejectReason,
      });
    }

    return undefined;
  })();

  const content = (() => {
    if (rejectComment) {
      return m.coInvestments_tracking_reject_tooltipComment({ rejectComment });
    }

    if (gpRejectSubreason) {
      return m.coInvestments_tracking_reject_gpTooltipSubreason({
        gpRejectSubreason,
      });
    }

    return undefined;
  })();

  return (
    <Tooltip
      content={content}
      providerProps={{ delayDuration: 0 }}
      title={title}
    >
      {children}
    </Tooltip>
  );
}

type TrackingTableProps = {
  data: DealTrackingLimitedPartnerBreakdown[];
  dealCurrency: Currency;
  isLoading?: boolean;
  dealId: string;
  totals?: DealTrackingTotals;
};

export const TrackingTable = ({
  data,
  dealCurrency,
  isLoading = false,
  dealId,
  totals,
}: TrackingTableProps) => {
  const { data: permissions } = useGetCoInvestGpPermissions(
    { dealId },
    {
      query: {
        select: (response) => response.data,
      },
    },
  );
  const canEditDealOpportunity = permissions?.canEditDealOpportunity ?? false;

  const { featureFlag } = useFeatureFlag();
  const isBookBuildEnabled =
    featureFlag[FeatureFlag.COINVEST_BOOK_BUILD_VD_26153];
  const isNewNavEnabled =
    featureFlag[FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769];

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const {
    isOpen: isBulkCloseDealModalOpen,
    setTrue: openBulkCloseDealModal,
    setFalse: closeBulkCloseDealModal,
  } = useBoolean(false);

  const { mutate: bulkCloseDeal, isPending: isBulkClosing } =
    useCloseDealOpportunities();

  const handleBulkCloseDeal = () => {
    const dealOpportunityIds = Object.keys(rowSelection).filter(
      (id) => id !== TOTAL_ROW_ID,
    );

    bulkCloseDeal(
      {
        data: { dealOpportunityIds },
      },
      {
        onSuccess: () => {
          closeBulkCloseDealModal();
          clearSelection();
          showToast({
            variant: "success",
            title: m.coInvestments_tracking_dealsClosedSuccessfully({
              count: dealOpportunityIds.length.toString(),
            }),
          });
          queryClient.invalidateQueries({
            queryKey: getGetDealTrackingForGpQueryKey(dealId),
          });
        },
        onError: (error) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_tracking_closeDeal_errorMessage(),
          });
        },
      },
    );
  };

  const {
    selectedItem,
    openAllocationModal,
    openIoiModal,
    openLpMinTicketSizeModal,
    openLpMaxTicketSizeModal,
    openGpEstimatedMinTicketSizeModal,
    openGpEstimatedMaxTicketSizeModal,
    openGpIndicativeOrderModal,
    closeModal,
    isModalOpen,
  } = useTrackingModals(canEditDealOpportunity);

  // Create total row from totals
  const totalRow = useMemo<DealTrackingLimitedPartnerBreakdown | null>(() => {
    if (!totals) return null;

    return {
      dealShortlistItemId:
        TOTAL_ROW_ID as DealTrackingLimitedPartnerBreakdown["dealShortlistItemId"],
      dealOpportunityId:
        TOTAL_ROW_ID as DealTrackingLimitedPartnerBreakdown["dealOpportunityId"],
      limitedPartnerId:
        TOTAL_ROW_ID as DealTrackingLimitedPartnerBreakdown["limitedPartnerId"],
      limitedPartnerName: m.common_total(),
      stage: "",
      tier: "",
      numberOfNdasSigned: 0,
      numberOfNdasIssued: 0,
      ndaStatus: "NOT_APPLICABLE" as const,
      isRejected: false,
      ticketSize: totals.ticketSize,
      indicativeAmount: totals.indicativeAmount,
      allocationAmount: totals.allocationAmount,
      gpIndicativeOrder: totals.gpIndicativeOrder,
      estimatedTicketSizeMin: totals.estimatedTicketSizeMin,
      estimatedTicketSizeMax: totals.estimatedTicketSizeMax,
      commitmentAmount: totals.commitmentAmount,
    };
  }, [totals]);

  const stageOptions = useMemo(
    () =>
      Array.from(
        new Set(
          data
            .map((r) => r.stage?.trim())
            .filter((stage): stage is string => isTruthy(stage)),
        ),
      )
        .sort((a, b) => a.localeCompare(b))
        .map((stage) => ({ value: stage, label: stage })),
    [data],
  );

  const tierOptions = useMemo(
    () =>
      Array.from(new Set(data.map((r) => r.tier).filter(isTruthy)))
        .sort((a, b) => Number(a) - Number(b))
        .map((t) => ({ value: t, label: t })),
    [data],
  );

  const filtersConfig = useMemo(
    () =>
      ({
        filters: [
          {
            id: "lpSearch",
            type: FilterType.Search,
            label: m.coInvestments_tracking_searchLps(),
            props: {
              placeholder: m.coInvestments_tracking_searchLps(),
              withBorder: true,
              className: "w-80",
            },
          },
          {
            id: "stage",
            type: FilterType.Dropdown,
            label: m.coInvestments_tracking_stage(),
            props: {
              placeholder: m.coInvestments_tracking_stage_placeholder({
                value: m.common_all(),
              }),
              renderValue: (label: string) =>
                m.coInvestments_tracking_stage_withValue({ label }),
              options: stageOptions,
              className: "w-56",
            },
          },
          {
            id: "tier",
            type: FilterType.Dropdown,
            label: m.coInvestments_tracking_tier(),
            props: {
              placeholder: m.coInvestments_tracking_tier_placeholder({
                value: m.common_all(),
              }),
              renderValue: (label: string) =>
                m.coInvestments_tracking_tier_withValue({ label }),
              options: tierOptions,
              className: "w-24",
            },
          },
        ],
        maxShownFilters: 3,
        showResetButton: true,
        noMarginBottom: true,
      }) as const satisfies TableFiltersConfig,
    [stageOptions, tierOptions],
  );

  const { filterState, hasActiveFilters, RenderFilters } = useTableFilters({
    id: TableFilterId.CoInvestmentsTracking,
    config: filtersConfig,
    persistInSession: false,
    syncWithURL: false,
  });

  const filteredData = useMemo(() => {
    const lpSearch = filterState.lpSearch;
    const stageFilter = filterState.stage?.value;
    const tierFilter = filterState.tier?.value;

    return data.filter((row) => {
      if (
        lpSearch &&
        !row.limitedPartnerName.toLowerCase().includes(lpSearch.toLowerCase())
      )
        return false;

      if (stageFilter && row.stage?.trim() !== stageFilter) return false;

      if (tierFilter && row.tier !== tierFilter) return false;

      return true;
    });
  }, [data, filterState]);

  // Prepend total row to data if it exists; suppress total row when filters are active
  const tableData = useMemo(() => {
    if (totalRow && !hasActiveFilters) {
      return [totalRow, ...filteredData];
    }

    return filteredData;
  }, [filteredData, totalRow, hasActiveFilters]);

  const { columns: exportColumns, groupedHeaders } = useTrackingExportColumns();
  const exportFilename = useExportFilename(dealId, "tracking");
  const { exportToCsv, exportToExcel, isExporting } = useTableExport({
    data: tableData,
    columns: exportColumns,
    filename: exportFilename,
    groupedHeaders,
  });

  const { clearSelection, selectedCount, hasSelection } = useTableSelection({
    data: filteredData,
    enableRowSelection: canEditDealOpportunity,
    selectedRows: rowSelection,
    onRowSelectionChange: setRowSelection,
    disableResetSelectionOnDataChange: true,
  });

  const columnGroups: ColumnGroup<
    DealTrackingLimitedPartnerBreakdown,
    unknown
  >[] = useMemo(() => {
    const bookBuildColumns: ColumnDef<DealTrackingLimitedPartnerBreakdown>[] =
      isBookBuildEnabled
        ? [
            {
              accessorKey: "probability",
              header: m.coInvestments_tracking_gpProbability(),
              align: "center",
              accessorFn: (row) => row.probability,
              sortUndefined: -1,
              cell: createCellWithTotalSupport(
                (row) => (
                  <TrackingTag
                    variant={row.probability == null ? "outline" : "default"}
                  >
                    {row.probability == null
                      ? EMPTY_CELL_VALUE
                      : `${row.probability}%`}
                  </TrackingTag>
                ),
                (row) =>
                  row.probability == null
                    ? EMPTY_CELL_VALUE
                    : `${row.probability}%`,
              ),
            },
            {
              accessorKey: "weightedOrder",
              header: m.coInvestments_tracking_gpWeightedOrder(),
              align: "center",
              accessorFn: (row) => row.weightedOrder?.amount,
              sortUndefined: -1,
              cell: createCellWithTotalSupport(
                (row) => (
                  <TrackingTag
                    variant={row.weightedOrder ? "default" : "outline"}
                  >
                    {row.weightedOrder
                      ? formatMoneyShort(row.weightedOrder)
                      : EMPTY_CELL_VALUE}
                  </TrackingTag>
                ),
                (row) =>
                  row.weightedOrder
                    ? formatMoneyShort(row.weightedOrder)
                    : EMPTY_CELL_VALUE,
              ),
            },
          ]
        : [];

    return [
      {
        label: "",
        columns: [
          {
            accessorKey: "limitedPartnerName",
            header: m.coInvestments_tracking_limitedPartner(),
            accessorFn: formatLimitedPartnerCellText,
            className: "text-left",
            cell: createCellWithTotalSupport((row) => (
              <span className="body-base-medium text-text/primary">
                {row.limitedPartnerName}
              </span>
            )),
          },
          {
            accessorKey: "stage",
            header: m.coInvestments_tracking_stage(),
            align: "center",
            accessorFn: formatStageCellText,
            cell: createCellWithTotalSupport((row) => (
              <RejectDetailsTooltip
                gpRejectReason={row.gpRejectReason}
                gpRejectSubreason={row.gpRejectSubreason}
                rejectComment={row.rejectComment}
                rejectReason={row.rejectReason}
              >
                <span className="body-base-regular text-text/primary">
                  {formatStageCellText(row)}
                </span>
              </RejectDetailsTooltip>
            )),
          },
          {
            accessorKey: "tier",
            header: m.coInvestments_tracking_tier(),
            align: "center",
            size: 32,
            accessorFn: (row) => (row.tier ? Number(row.tier) : undefined),
            sortUndefined: -1,
            cell: ({ row }) => {
              if (isTotalRow(row.original)) {
                return (
                  <span className="body-base-semibold">{EMPTY_CELL_VALUE}</span>
                );
              }

              return (
                <CounterBadge
                  className="bg-background/tertiary"
                  count={row.original.tier}
                  variant="rounded"
                />
              );
            },
          },
          {
            accessorKey: "numberOfNdasSigned",
            header: m.coInvestments_tracking_nda(),
            align: "center",
            accessorFn: (row) =>
              row.numberOfNdasIssued > 0
                ? // Primary: signed ratio; tiebreaker: fewer issued ranks higher
                  row.numberOfNdasSigned / row.numberOfNdasIssued -
                  row.numberOfNdasIssued * 1e-7
                : undefined,
            sortUndefined: -1,
            cell: ({ row }) => {
              if (isTotalRow(row.original)) {
                return (
                  <span className="body-base-semibold text-text/primary">
                    {EMPTY_CELL_VALUE}
                  </span>
                );
              }

              const { numberOfNdasIssued } = row.original;
              let ndaVariant: "default" | "outline" | "pending" | "positive";

              if (numberOfNdasIssued === 0) {
                ndaVariant = "outline";
              } else if (areAllNdasSigned(row.original)) {
                ndaVariant = "positive";
              } else if (hasUnsignedNdas(row.original)) {
                ndaVariant = "pending";
              } else {
                ndaVariant = "default";
              }

              return (
                <TrackingTag variant={ndaVariant}>
                  {formatNdaCellText(row.original)}
                </TrackingTag>
              );
            },
          },
        ],
      },
      {
        label: m.coInvestments_tracking_lpGroup(),
        columns: [
          {
            accessorKey: "ticketSize.minimum",
            header: m.coInvestments_tracking_lpMinimumTicketSize(),
            align: "center",
            accessorFn: (row) => row.ticketSize?.minimum.amount,
            sortUndefined: -1,
            cell: createCellWithTotalSupport(
              (row) => (
                <TrackingTag
                  asButton={canOpenModalForItem(canEditDealOpportunity, row)}
                  dataTestId={`ticket-size-min-${row.limitedPartnerId}`}
                  onClick={() => openLpMinTicketSizeModal(row)}
                  variant={row.ticketSize ? "positive" : "outline"}
                >
                  {formatLpTicketSizeMinCellText(row)}
                </TrackingTag>
              ),
              formatLpTicketSizeMinCellText,
            ),
          },
          {
            accessorKey: "ticketSize.maximum",
            header: m.coInvestments_tracking_lpMaximumTicketSize(),
            align: "center",
            accessorFn: (row) => row.ticketSize?.maximum.amount,
            sortUndefined: -1,
            cell: createCellWithTotalSupport(
              (row) => (
                <TrackingTag
                  asButton={canOpenModalForItem(canEditDealOpportunity, row)}
                  dataTestId={`ticket-size-max-${row.limitedPartnerId}`}
                  onClick={() => openLpMaxTicketSizeModal(row)}
                  variant={row.ticketSize ? "positive" : "outline"}
                >
                  {formatLpTicketSizeMaxCellText(row)}
                </TrackingTag>
              ),
              formatLpTicketSizeMaxCellText,
            ),
          },
          {
            accessorKey: "indicativeAmount",
            header: m.coInvestments_tracking_indicativeOrder(),
            align: "center",
            accessorFn: (row) => row.indicativeAmount?.amount,
            sortUndefined: -1,
            cell: createCellWithTotalSupport(
              (row) => (
                <TrackingTag
                  asButton={canOpenModalForItem(canEditDealOpportunity, row)}
                  dataTestId={`indication-of-interest-${row.limitedPartnerId}`}
                  onClick={() => openIoiModal(row)}
                  variant={row.indicativeAmount ? "positive" : "outline"}
                >
                  {formatIndicativeAmountCellText(row)}
                </TrackingTag>
              ),
              formatIndicativeAmountCellText,
            ),
          },
        ],
      },
      {
        label: m.coInvestments_tracking_gpGroup(),
        columns: [
          {
            accessorKey: "estimatedTicketSizeMin",
            header: m.coInvestments_tracking_gpMinimumTicketSize(),
            align: "center",
            accessorFn: (row) => row.estimatedTicketSizeMin?.amount,
            sortUndefined: -1,
            cell: createCellWithTotalSupport(
              (row) => (
                <TrackingTag
                  asButton={canOpenModalForItem(canEditDealOpportunity, row)}
                  dataTestId={`gp-estimated-min-${row.limitedPartnerId}`}
                  onClick={() => openGpEstimatedMinTicketSizeModal(row)}
                  variant={row.estimatedTicketSizeMin ? "positive" : "outline"}
                >
                  {formatGpEstimatedTicketSizeMinCellText(row)}
                </TrackingTag>
              ),
              formatGpEstimatedTicketSizeMinCellText,
            ),
          },
          {
            accessorKey: "estimatedTicketSizeMax",
            header: m.coInvestments_tracking_gpMaximumTicketSize(),
            align: "center",
            accessorFn: (row) => row.estimatedTicketSizeMax?.amount,
            sortUndefined: -1,
            cell: createCellWithTotalSupport(
              (row) => (
                <TrackingTag
                  asButton={canOpenModalForItem(canEditDealOpportunity, row)}
                  dataTestId={`gp-estimated-max-${row.limitedPartnerId}`}
                  onClick={() => openGpEstimatedMaxTicketSizeModal(row)}
                  variant={row.estimatedTicketSizeMax ? "positive" : "outline"}
                >
                  {formatGpEstimatedTicketSizeMaxCellText(row)}
                </TrackingTag>
              ),
              formatGpEstimatedTicketSizeMaxCellText,
            ),
          },
          {
            accessorKey: "gpIndicativeOrder",
            header: m.coInvestments_tracking_gpIndicativeOrder(),
            align: "center",
            accessorFn: (row) => row.gpIndicativeOrder?.amount,
            sortUndefined: -1,
            cell: createCellWithTotalSupport(
              (row) => (
                <TrackingTag
                  asButton={canOpenModalForItem(canEditDealOpportunity, row)}
                  dataTestId={`gp-indicative-order-${row.limitedPartnerId}`}
                  onClick={() => openGpIndicativeOrderModal(row)}
                  variant={row.gpIndicativeOrder ? "positive" : "outline"}
                >
                  {formatGpIndicativeOrderCellText(row)}
                </TrackingTag>
              ),
              formatGpIndicativeOrderCellText,
            ),
          },
          ...bookBuildColumns,
        ],
      },
      {
        label: "",
        columns: [
          {
            accessorKey: "allocationAmount",
            header: m.coInvestments_tracking_allocationTitle(),
            align: "center",
            accessorFn: (row) => row.allocationAmount?.amount,
            sortUndefined: -1,
            cell: createCellWithTotalSupport((row) => {
              const amountTagVariant = (() => {
                if (row.isRejected) return "negative";

                if (!row.allocationAmount) return "outline";

                return "positive";
              })();

              const canEditAllocation =
                canOpenModalForItem(canEditDealOpportunity, row) &&
                !row.commitmentAmount &&
                !row.isRejected;

              return (
                <RejectDetailsTooltip
                  gpRejectReason={row.gpRejectReason}
                  gpRejectSubreason={row.gpRejectSubreason}
                  rejectComment={row.rejectComment}
                  rejectReason={row.rejectReason}
                >
                  <TrackingTag
                    asButton={canEditAllocation}
                    dataTestId={`allocation-amount-${row.limitedPartnerId}`}
                    onClick={
                      canEditAllocation
                        ? () => openAllocationModal(row)
                        : undefined
                    }
                    variant={amountTagVariant}
                  >
                    {formatAllocationAmountCellText(row)}
                  </TrackingTag>
                </RejectDetailsTooltip>
              );
            }, formatAllocationAmountCellText),
          },
          {
            accessorKey: "commitmentAmount",
            header: m.coInvestments_tracking_commitmentAmount(),
            align: "center",
            accessorFn: (row) => row.commitmentAmount?.amount,
            sortUndefined: -1,
            cell: createCellWithTotalSupport(
              (row) => (
                <div className="flex flex-col items-center">
                  <span className="body-base-medium text-text/primary">
                    {formatCommitmentAmountCellText(row)}
                  </span>
                  {row.isClosed ? (
                    <TrackingTag
                      dataTestId={`closed-tag-${row.limitedPartnerId}`}
                      variant="disabled"
                    >
                      {m.coInvestments_tracking_closed_tag()}
                    </TrackingTag>
                  ) : null}
                </div>
              ),
              formatCommitmentAmountCellText,
            ),
          },
          {
            cell: ({ row }) => {
              if (isTotalRow(row.original)) {
                return null;
              }

              return (
                <TrackingRowActionsMenu
                  dealId={dealId}
                  trackingItem={row.original}
                />
              );
            },
            ...ACTION_COLUMN_DEFAULT_PROPS,
            minSize: 64,
            className: cnTwMerge(
              ACTION_COLUMN_DEFAULT_PROPS.className,
              "px-1.5",
            ),
          },
        ],
      },
    ];
  }, [
    canEditDealOpportunity,
    isBookBuildEnabled,
    openLpMinTicketSizeModal,
    openLpMaxTicketSizeModal,
    openIoiModal,
    openGpEstimatedMinTicketSizeModal,
    openGpEstimatedMaxTicketSizeModal,
    openGpIndicativeOrderModal,
    openAllocationModal,
    dealId,
  ]);

  return (
    <>
      {hasSelection ? (
        <BulkActions
          isLoading={isBulkClosing}
          onClear={clearSelection}
          primaryAction={{
            children: m.coInvestments_tracking_closeDeals(),
            onClick: openBulkCloseDealModal,
          }}
          selectedCount={selectedCount}
          selectionLabel={{
            single: m.deal_deal(),
            multiple: m.deal_deals(),
          }}
        />
      ) : null}
      <h2 className="heading-5 text-text/primary mb-4">
        {m.coInvestments_tracking_limitedPartners()}
      </h2>
      <div className="flex flex-wrap justify-between gap-4 mb-4">
        <div className="flex-1">{RenderFilters}</div>
        {isNewNavEnabled ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ThemedButton
                dataTestId="tracking-table-export"
                disabled={isExporting}
                icon={<DownloadIcon />}
                size="small"
                variant="secondary"
              >
                {m.btn_export()}
              </ThemedButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="p-2">
              <DropdownMenuItem onSelect={exportToCsv}>
                <DownloadIcon className="w-4 h-4" />
                <p className="body-base-medium">{m.btn_exportAsCsv()}</p>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={exportToExcel}>
                <DownloadIcon className="w-4 h-4" />
                <p className="body-base-medium">{m.btn_exportAsExcel()}</p>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      <div data-testid="tracking-table">
        <GroupedHeaderTable
          classNames={{
            groupHeader: "px-1.5 py-1",
            header: "px-1.5",
            cell: "px-1.5",
          }}
          columnGroups={columnGroups}
          data={tableData}
          dataTestId="tracking-core-table"
          dimUnselectableRowsOnMobile={false}
          emptyState={{
            title: hasActiveFilters
              ? m.coInvestments_tracking_noFilterResults()
              : m.coInvestments_tracking_noDataTitle(),
            description: hasActiveFilters ? undefined : (
              <>
                {m.trackingtable_emptyState_uploadPrefix()}{" "}
                <Link
                  className="text-text/secondary underline"
                  data-testid="go-to-prospects-button"
                  to={CoInvestmentFullPaths.CoInvestmentProspects.replace(
                    ":id",
                    dealId,
                  )}
                >
                  {m.trackingtable_emptyState_prospectsLinkText()}
                </Link>{" "}
                {m.trackingtable_emptyState_uploadSuffix()}
              </>
            ),
            illustration: <ListIllustration />,
          }}
          getIsPinnedRow={isTotalRow}
          getRowId={(row) => row.dealOpportunityId}
          isLoading={isLoading}
          noSorting={false}
          selectionConfig={
            canEditDealOpportunity
              ? {
                  selectedRows: rowSelection,
                  onRowSelectionChange: setRowSelection,
                  getCanSelect: (row) =>
                    !row.isClosed && !row.isRejected && !!row.commitmentAmount,
                  hideUnselectableCheckboxes: true,
                  disableResetSelectionOnDataChange: true,
                }
              : undefined
          }
        />
      </div>
      {isModalOpen("allocation") && selectedItem ? (
        <AllocationAmountModal
          allocationAmount={selectedItem.allocationAmount}
          dealCurrency={dealCurrency}
          dealId={dealId}
          dealOpportunityId={selectedItem.dealOpportunityId}
          indicativeAmount={selectedItem.indicativeAmount}
          isOpen={isModalOpen("allocation")}
          onClose={closeModal}
        />
      ) : null}
      {isModalOpen("ioi") && selectedItem ? (
        <EditIndicationOfInterestModal
          dealCurrency={dealCurrency}
          dealId={dealId}
          dealOpportunityId={selectedItem.dealOpportunityId}
          isOpen={isModalOpen("ioi")}
          item={selectedItem}
          onClose={closeModal}
        />
      ) : null}
      {isModalOpen("lpMinTicketSize") && selectedItem ? (
        <EditMinTicketSizeModal
          dealCurrency={dealCurrency}
          dealId={dealId}
          dealOpportunityId={selectedItem.dealOpportunityId}
          initialValues={selectedItem.ticketSize}
          isOpen={isModalOpen("lpMinTicketSize")}
          onClose={closeModal}
        />
      ) : null}
      {isModalOpen("lpMaxTicketSize") && selectedItem ? (
        <EditMaxTicketSizeModal
          dealCurrency={dealCurrency}
          dealId={dealId}
          dealOpportunityId={selectedItem.dealOpportunityId}
          initialValues={selectedItem.ticketSize}
          isOpen={isModalOpen("lpMaxTicketSize")}
          onClose={closeModal}
        />
      ) : null}
      {isModalOpen("gpEstimatedMinTicketSize") && selectedItem ? (
        <EditEstimatedMinTicketSizeModal
          dealCurrency={dealCurrency}
          dealId={dealId}
          dealOpportunityId={selectedItem.dealOpportunityId}
          initialValues={{
            estimatedTicketSizeMin: selectedItem.estimatedTicketSizeMin,
            estimatedTicketSizeMax: selectedItem.estimatedTicketSizeMax,
            gpIndicativeOrder: selectedItem.gpIndicativeOrder,
          }}
          isOpen={isModalOpen("gpEstimatedMinTicketSize")}
          onClose={closeModal}
        />
      ) : null}
      {isModalOpen("gpEstimatedMaxTicketSize") && selectedItem ? (
        <EditEstimatedMaxTicketSizeModal
          dealCurrency={dealCurrency}
          dealId={dealId}
          dealOpportunityId={selectedItem.dealOpportunityId}
          initialValues={{
            estimatedTicketSizeMin: selectedItem.estimatedTicketSizeMin,
            estimatedTicketSizeMax: selectedItem.estimatedTicketSizeMax,
            gpIndicativeOrder: selectedItem.gpIndicativeOrder,
          }}
          isOpen={isModalOpen("gpEstimatedMaxTicketSize")}
          onClose={closeModal}
        />
      ) : null}
      {isModalOpen("gpIndicativeOrder") && selectedItem ? (
        <EditIndicativeOrderModal
          dealCurrency={dealCurrency}
          dealId={dealId}
          dealOpportunityId={selectedItem.dealOpportunityId}
          initialValues={{
            gpIndicativeOrder: selectedItem.gpIndicativeOrder,
            estimatedTicketSizeMin: selectedItem.estimatedTicketSizeMin,
            estimatedTicketSizeMax: selectedItem.estimatedTicketSizeMax,
          }}
          isOpen={isModalOpen("gpIndicativeOrder")}
          onClose={closeModal}
        />
      ) : null}
      <BulkCloseDealModal
        isLoading={isBulkClosing}
        isOpen={isBulkCloseDealModalOpen}
        onClose={closeBulkCloseDealModal}
        onConfirm={handleBulkCloseDeal}
        selectedCount={selectedCount}
      />
    </>
  );
};
